import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaFlask, FaCode, FaCoffee, FaBug } from 'react-icons/fa';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const stats = [
    { icon: FaCode, value: '20+', label: 'Projects Cooked' },
    { icon: FaFlask, value: '99.1%', label: 'Code Purity' },
    { icon: FaCoffee, value: '500+', label: 'Cups of Coffee' },
    { icon: FaBug, value: '∞', label: 'Bugs Eliminated' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="about" className="py-20 px-4 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 50px,
            rgba(0, 200, 83, 0.1) 50px,
            rgba(0, 200, 83, 0.1) 51px
          ),
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 50px,
            rgba(0, 200, 83, 0.1) 50px,
            rgba(0, 200, 83, 0.1) 51px
          )`
        }} />
      </div>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="max-w-6xl mx-auto relative z-10"
      >
        {/* Section Title */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-white">The </span>
            <span className="text-bb-green-light">Chemist's</span>
            <span className="text-white"> Story</span>
          </h2>
          <div className="w-24 h-1 bg-bb-green mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image/Avatar Section */}
          <motion.div variants={itemVariants} className="relative">
            <div className="relative w-64 h-64 sm:w-80 sm:h-80 mx-auto">
              {/* Hazmat Suit Frame */}
              <motion.div
                className="absolute inset-0 border-4 border-bb-green rounded-lg"
                animate={{
                  boxShadow: [
                    "0 0 20px rgba(0, 200, 83, 0.3)",
                    "0 0 40px rgba(0, 200, 83, 0.6)",
                    "0 0 20px rgba(0, 200, 83, 0.3)"
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />

              {/* Profile Image Placeholder */}
              <div className="absolute inset-2 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center overflow-hidden">
                <div className="text-center">
                  <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto bg-bb-green rounded-full flex items-center justify-center mb-4">
                    <span className="text-5xl sm:text-6xl font-bold text-white">JM</span>
                  </div>
                  <p className="text-bb-green-light text-sm">Heisenberg of Code</p>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute -top-4 -right-4 w-16 h-16 bg-gray-800 border-2 border-bb-green flex items-center justify-center"
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              >
                <span className="text-bb-green-light font-bold">Br</span>
              </motion.div>

              <motion.div
                className="absolute -bottom-4 -left-4 w-14 h-14 bg-gray-800 border-2 border-bb-green flex items-center justify-center"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span className="text-bb-green-light font-bold">Ba</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Bio Section */}
          <motion.div variants={itemVariants} className="text-center md:text-left">
            <h3 className="text-2xl font-bold text-white mb-4">
              Hello, I'm <span className="text-bb-green-light">Jiten Mahajan</span>
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              A passionate Full-Stack Developer and B.Tech graduate from Government College Dharamshala
              with expertise in synthesizing elegant web solutions. Like Walter White transformed chemistry,
              I transform ideas into pixel-perfect, high-performance applications.
            </p>
            <p className="text-gray-400 mb-6 leading-relaxed">
              My expertise lies in the React ecosystem, Node.js, Python, and modern web technologies.
              I specialize in building scalable applications using MERN stack, Django, and cloud platforms.
              I believe in writing code that's not just functional, but
              <span className="text-bb-green-light"> 99.1% pure</span> – clean, maintainable,
              and scalable.
            </p>
            <p className="text-gray-500 italic mb-8">
              "I am not in danger, Skyler. I am the danger. I am the one who codes!"
            </p>

            <motion.a
              href="/JitenMahajan.pdf"
              download="JitenMahajan_Resume.pdf"
              className="inline-flex items-center gap-2 px-6 py-3 bg-bb-green text-white font-bold rounded-lg hover:bg-bb-green-light transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaFlask />
              Download My Formula (Resume)
            </motion.a>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="bg-gray-800/50 border border-green-900 rounded-lg p-6 text-center"
              whileHover={{
                scale: 1.05,
                borderColor: 'rgba(0, 200, 83, 0.8)',
                boxShadow: '0 0 20px rgba(0, 200, 83, 0.3)'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 + index * 0.1 }}
            >
              <stat.icon className="text-3xl text-bb-green-light mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default About;
